import { motion } from 'motion/react'
import { useRef } from 'react'
import { TOKENS } from '../ui/dashboard-primitives'
import { PanelTitle } from '../ui/PanelTitle'
import { useReveal } from '../../lib/motion'
import { retryUnreachable, type QueryReachability } from '../../lib/queryState'
import { useReassured } from './reassurance'

/**
 * The house treatment for data that could not be loaded, for every panel in
 * the web client.
 *
 * It was the Journey's until Spec 2 D5 (#169) promoted it: the Journey had the
 * one error state that separates a failure from an absence, and the dashboard
 * rendered every outage as an empty state instead. A panel reaches it through
 * `AsyncPanel`, which offers no way to render anything else on failure.
 */

/**
 * The shared terracotta note both error states are built from.
 *
 * Three things make it unmistakable next to an empty state at a glance, and all
 * three are deliberate:
 *
 * 1. **A tinted surface, not a bare paragraph.** `--terracotta-soft` is the
 *    design system's alert background, and a wash is how this app separates
 *    things — never a rule or a border (the No-Line Rule). The wash is
 *    translucent, so it is laid over the note's own `--warm-dark` fill: the
 *    Journey always put the note on a dark panel, but the dashboard can put it
 *    straight on the page gradient, whose tan band left the terracotta label
 *    near-invisible (#170). DESIGN.md names `--warm-dark` for floating
 *    surfaces; the note borrows it as the palette's one opaque-enough dark
 *    fill, a deliberate reuse rather than something DESIGN.md prescribes.
 * 2. **A labelled state.** An empty state is one sentence and nothing else;
 *    this one opens on a mono eyebrow in terracotta, the same shape the
 *    section headers use, so the reader is told what they are looking at
 *    before they read a word of prose. The label takes `--warn-terracotta-text`
 *    rather than `--warn-terracotta`: at 10px it needs 4.5:1, and the plain
 *    token measured 3.5:1 over the tan peak (PanelError.test.tsx measures it).
 * 3. **A brighter text tier.** An empty state recedes at `--cream-faint`; this
 *    sits at `--cream-soft`, because "we could not load this" is information
 *    the reader needs, not a footnote about their own history.
 *
 * Calm, though, is the whole brief: no red, no icon that means "stop", no
 * pulse, no bespoke entrance. It reuses `useReveal` unchanged — the same fade
 * every other block arrives with, reduced-motion handling and all. A
 * data-loading failure that a reader meets once a year does not earn a motion
 * signature of its own; giving it one would read as alarm.
 *
 * Nothing about the underlying failure reaches the page: no status code, no
 * endpoint, no error object. The reader can do nothing with any of it. What
 * they can do is ask again, so the note ends on a quiet "Try again": text, not
 * a filled button, because recovering is the one action and it should not
 * shout. It is a polite status region, so a screen reader hears the failure
 * instead of silence when the skeleton gives way to it.
 *
 * Its prose is the caller's `lead` followed by the reassurance, when this is the
 * note on the page that says it (#212). A section note has no lead, so without
 * the reassurance it is the label, the title and Try again.
 */
function ErrorNote({
  lead,
  title,
  sources,
}: {
  lead?: string
  title?: string
  sources: Partial<QueryReachability>[]
}) {
  const reveal = useReveal()
  const ref = useRef<HTMLDivElement>(null)
  const prose = useReassured(ref, lead)
  return (
    <motion.div
      {...reveal}
      ref={ref}
      role="status"
      data-panel-error
      className="rounded-2xl p-5 sm:p-6"
      style={{
        backgroundColor: TOKENS.warmDark,
        backgroundImage: `linear-gradient(${TOKENS.terracottaSoft}, ${TOKENS.terracottaSoft})`,
      }}
    >
      <p
        className="flex items-center gap-2.5 text-[10px] tracking-[0.28em] uppercase"
        style={{ fontFamily: 'var(--font-mono)', color: TOKENS.terracottaText }}
      >
        <span
          aria-hidden
          className="inline-block rounded-full shrink-0"
          style={{ width: 7, height: 7, background: TOKENS.terracotta }}
        />
        Could not load
      </p>
      {title && <PanelTitle>{title}</PanelTitle>}
      {prose && (
        <p
          className="mt-3 text-sm leading-relaxed max-w-[54ch]"
          style={{ fontFamily: 'var(--font-body)', color: TOKENS.creamSoft }}
        >
          {prose}
        </p>
      )}
      <TryAgain sources={sources} />
    </motion.div>
  )
}

/**
 * The one way out of any failure state: ask for the failed requests again.
 *
 * Exported because the Journey's hero says a failure in its own editorial voice
 * rather than in this note, and a second button would drift from this one in
 * shape, wording and target size. 44px tall so it is a real tap target on a
 * phone; the negative margin keeps its text aligned with the prose above it
 * rather than indented.
 */
export function TryAgain({ sources }: { sources: Partial<QueryReachability>[] }) {
  return (
    <button
      type="button"
      onClick={() => retryUnreachable(sources)}
      className="mt-2 -ml-2 inline-flex min-h-[44px] items-center rounded-lg px-2 text-[10px] tracking-[0.28em] uppercase transition-colors duration-200 ease-out hover:text-(--accent-amber) outline-none focus-visible:ring-2 focus-visible:ring-(--accent-amber)"
      style={{ fontFamily: 'var(--font-mono)', color: TOKENS.cream }}
    >
      Try again
    </button>
  )
}

/**
 * One panel's data could not be loaded. Sits where the panel's content would,
 * under the panel's own head, so the reader still knows which part failed.
 *
 * `subject` is a capitalised noun phrase naming what is missing — "Your session
 * history", not "session history" — because it opens the sentence. `sources`
 * are what the panel loads; "Try again" asks again for the ones that failed.
 */
export function PanelError({ subject, sources }: { subject: string; sources: Partial<QueryReachability>[] }) {
  return (
    <ErrorNote lead={`${subject} could not be loaded just now.`} sources={sources} />
  )
}

/**
 * A whole section could not be loaded: one note in place of all of its panels.
 *
 * The collapse is the entire reason a page can be honest without becoming a
 * wall of apologies. When the API is down every panel fails for the same single
 * reason, and ten identical terracotta cards down a page say that reason ten
 * times while looking like ten separate faults. One note says it once. The
 * caller supplies the surface it sits on.
 *
 * The same note stands for a whole page when every section failed (the A+
 * ruling on #169), through `PageError`. `sources` are every request behind what
 * it replaces, so its "Try again" asks again for all of them that failed.
 */
export function SectionError({
  subject,
  sources,
}: {
  subject: string
  sources: Partial<QueryReachability>[]
}) {
  return <ErrorNote title={`${subject} could not be loaded`} sources={sources} />
}

/**
 * A whole page could not be loaded: the A+ ruling's third rung, one card in place
 * of the page (#169). The section note, in the page's own frame.
 *
 * The frame is here rather than on each page because the page cards used to sit
 * at three slightly different offsets (the Dashboard's, Sessions' and Profile's),
 * so moving between pages during an outage made the one card on screen jump
 * (Spec 2 D5, #213). It takes the 1200px column most pages already use; the
 * Dashboard and the Journey run wider only because their charts need it, and a
 * note has no chart. The page keeps its own `h1`.
 */
export function PageError({
  subject,
  sources,
}: {
  subject: string
  sources: Partial<QueryReachability>[]
}) {
  return (
    <div data-page-error className="max-w-[1200px] mx-auto pt-12 lg:pt-16 pb-20">
      <SectionError subject={subject} sources={sources} />
    </div>
  )
}
